import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Upload,
  Download,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { PageTemplate } from '@/components/templates'
import { Input } from '@/components/atoms'
import { CONTAINER_SIZES } from '@/constants'
import { formStyles, typography } from '@/constants/styles'

const PortfolioCreateScreen = () => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<'manual' | 'csv'>('manual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Manual creation form
  const [portfolioName, setPortfolioName] = useState('')
  const [portfolioDescription, setPortfolioDescription] = useState('')

  // CSV import form
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPortfolioName, setCsvPortfolioName] = useState('')
  const [csvPreview, setCsvPreview] = useState<any[]>([])
  const [csvValidation, setCsvValidation] = useState<{
    isValid: boolean
    errors: string[]
    warnings: string[]
  }>({ isValid: false, errors: [], warnings: [] })

  const handleManualCreate = async () => {
    if (!portfolioName.trim()) {
      setError('Portfolio name is required')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const newPortfolio = await portfolioTrackerService.createPortfolio(portfolioName.trim())
      setSuccessMessage('Portfolio created successfully!')

      setTimeout(() => {
        navigate(`/portfolio/${newPortfolio.portfolio_id}`)
      }, 1500)
    } catch (err) {
      setError('Failed to create portfolio')
      console.error('Error creating portfolio:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCSVImport = async () => {
    if (!csvFile || !csvPortfolioName.trim()) {
      setError('Please select a CSV file and enter portfolio name')
      return
    }

    if (!csvValidation.isValid) {
      setError('Please fix CSV validation errors before importing')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const result = await portfolioTrackerService.createPortfolioFromCSV(csvPortfolioName.trim(), csvFile)
      setSuccessMessage(`Portfolio created with ${result.matched_assets} assets!`)

      setTimeout(() => {
        navigate(`/portfolio/${result.portfolio_id}`)
      }, 1500)
    } catch (err: any) {
      if (portfolioTrackerService.isCSVError(err)) {
        setError(`CSV Error: ${err.error}`)
      } else {
        setError('Failed to import CSV')
      }
      console.error('Error importing CSV:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setCsvFile(file)
      setError(null)
      validateCSV(file)
    } else {
      setError('Please select a valid CSV file')
      setCsvFile(null)
      setCsvPreview([])
      setCsvValidation({ isValid: false, errors: [], warnings: [] })
    }
  }

  const validateCSV = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      const lines = text.split('\n')
      const headers = lines[0]?.split(',').map(h => h.trim().toLowerCase())

      const requiredColumns = ['project name', 'date of investment', 'invested amount']
      const optionalColumns = ['project website', 'project valuation', 'amount of equity or tokens', 'terms']

      const errors: string[] = []
      const warnings: string[] = []

      // Check required columns
      const missingColumns = requiredColumns.filter(col => !headers?.includes(col))
      if (missingColumns.length > 0) {
        errors.push(`Missing required columns: ${missingColumns.join(', ')}`)
      }

      // Check for unknown columns
      const allValidColumns = [...requiredColumns, ...optionalColumns]
      const unknownColumns = headers?.filter(col => !allValidColumns.includes(col)) || []
      if (unknownColumns.length > 0) {
        warnings.push(`Unknown columns will be ignored: ${unknownColumns.join(', ')}`)
      }

      // Parse preview data
      const previewData = lines.slice(1, 6).map(line => {
        const values = line.split(',')
        const row: any = {}
        headers?.forEach((header, index) => {
          row[header] = values[index]?.trim() || ''
        })
        return row
      }).filter(row => Object.values(row).some(val => val !== ''))

      setCsvPreview(previewData)
      setCsvValidation({
        isValid: errors.length === 0,
        errors,
        warnings
      })
    }
    reader.readAsText(file)
  }

  const downloadCSVTemplate = () => {
    const template = `Project Name,Date of Investment,Invested Amount,Project Website,Project Valuation,Amount of equity or tokens,Terms
Example Project Alpha,2023-01-15,50000,https://example-alpha.com,1000000,100000,SAFE agreement
Example Project Beta,2023-02-20,75000,https://example-beta.com,1500000,150000,Equity investment
Example Project Gamma,2023-03-10,25000,https://example-gamma.com,500000,50000,Token warrant`

    const blob = new Blob([template], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'portfolio_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <PageTemplate
      maxWidth={CONTAINER_SIZES.WIDE}
    >
      <div className="relative">
      {successMessage && (
        <div className="mb-4 border border-[#1fb845]/30 bg-[#1fb845]/12 px-4 py-3 text-sm text-[#1d6f33] dark:border-[#1fb845]/40 dark:bg-[#1fb845]/12 dark:text-[#7ae299]">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 border border-[#ff540c]/35 bg-[#ff540c]/12 px-4 py-3 text-sm text-[#923200] dark:border-[#ff540c]/45 dark:bg-[#ff540c]/12 dark:text-[#ffb08a]">
          {error}
        </div>
      )}

      <div className="md:hidden space-y-4">
        <div>
          <button
            onClick={() => navigate('/portfolio')}
            className="inline-flex w-fit items-center gap-1.5 border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] px-4 py-2 text-[12px] font-medium text-[#2d2923] transition-colors hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]"
          >
            <ArrowLeft size={13} />
            Back to Portfolio
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={downloadCSVTemplate}
            className="inline-flex items-center gap-1.5 border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] px-4 py-2 text-[12px] font-medium text-[#2d2923] transition-colors hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]"
          >
            <Download size={13} />
            Download Template
          </button>

          <button
            onClick={() => setActiveTab('csv')}
            className={`inline-flex items-center gap-1.5 border px-4 py-2 text-[12px] font-medium transition-colors ${
              activeTab === 'csv'
                ? 'border-[rgba(45,41,35,0.12)] bg-[rgba(45,41,35,0.1)] text-[#2d2923] hover:bg-[rgba(45,41,35,0.14)] dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(253,253,253,0.1)] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.16)]'
                : 'border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] text-[#2d2923] hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]'
            }`}
          >
            <Upload size={13} />
            Import CSV
          </button>

          <button
            onClick={() => setActiveTab('manual')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-medium transition-colors ${
              activeTab === 'manual'
                ? 'bg-[#2d2923] text-white hover:bg-[#201d19] dark:bg-white dark:text-[#0d0d0d] dark:hover:bg-[rgba(253,253,253,0.9)]'
                : 'border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] text-[#2d2923] hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]'
            }`}
          >
            <Plus size={13} />
            Manual Creation
          </button>
        </div>

        <div className="w-full border border-white/80 bg-[rgba(255,255,255,0.72)] p-5 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
          {activeTab === 'manual' ? (
            <>
              <h1 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                Create Portfolio Manually
              </h1>
              <p className="mt-1 text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                Create an empty portfolio and add assets later
              </p>

              <div className="mt-5 space-y-5">
                <div>
                  <label className={typography.labelBlock}>Portfolio Name</label>
                  <Input
                    value={portfolioName}
                    onChange={(e) => setPortfolioName(e.target.value)}
                    placeholder="Enter portfolio name"
                    onKeyDown={(e) => e.key === 'Enter' && handleManualCreate()}
                    className={`${formStyles.input} !h-[48px] !rounded-none !shadow-none dark:!bg-[#1c1c1c] dark:!border-[rgba(255,255,255,0.12)] dark:!text-[#fdfdfd] dark:placeholder:!text-[rgba(253,253,253,0.72)]`}
                  />
                </div>

                <div>
                  <label className={typography.labelBlock}>Description (Optional)</label>
                  <textarea
                    value={portfolioDescription}
                    onChange={(e) => setPortfolioDescription(e.target.value)}
                    placeholder="Add a description for your portfolio"
                    className="h-[143px] w-full resize-none border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.32)] px-3 py-3 text-[14px] text-[#2d2923] placeholder:text-[rgba(45,41,35,0.64)] focus:outline-none dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1c1c1c] dark:text-[#fdfdfd] dark:placeholder:text-[rgba(253,253,253,0.72)]"
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <button
                    onClick={() => navigate('/portfolio')}
                    className="h-[46px] w-full border border-[rgba(45,41,35,0.48)] bg-[rgba(255,255,255,0.72)] text-[14px] font-medium uppercase text-[#2d2923] transition-colors hover:bg-white dark:border-white dark:bg-[rgba(45,41,35,0.72)] dark:text-white dark:hover:bg-[rgba(45,41,35,0.9)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleManualCreate}
                    disabled={loading || !portfolioName.trim()}
                    className="h-[46px] w-full bg-[#2d2923] text-[14px] font-medium uppercase text-white transition-colors hover:bg-[#201d19] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-[#ecebeb] dark:text-[#2d2923] dark:hover:bg-[#dfdede]"
                  >
                    {loading ? 'Creating...' : 'Create Portfolio'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                Import Portfolio from CSV
              </h1>
              <p className="mt-1 text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                Create a portfolio with assets from a CSV file
              </p>

              <div className="mt-5 space-y-5">
                <div>
                  <label className={typography.labelBlock}>Portfolio Name</label>
                  <Input
                    value={csvPortfolioName}
                    onChange={(e) => setCsvPortfolioName(e.target.value)}
                    placeholder="Enter portfolio name"
                    className={`${formStyles.input} !h-[48px] !rounded-none !shadow-none dark:!bg-[#1c1c1c] dark:!border-[rgba(255,255,255,0.12)] dark:!text-[#fdfdfd] dark:placeholder:!text-[rgba(253,253,253,0.72)]`}
                  />
                </div>

                <div>
                  <label className={typography.labelBlock}>CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="h-[48px] w-full border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.52)] px-3 py-[11px] text-sm text-[#2d2923] file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1c1c1c] dark:text-[#fdfdfd] dark:file:text-[#fdfdfd]"
                  />
                  <p className="mt-1 text-xs text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                    Required columns: Project Name, Date of Investment, Invested Amount
                  </p>
                </div>

                {csvValidation.errors.length > 0 && (
                  <div className="border border-[#ff540c]/20 bg-[#ff540c]/8 p-4 dark:border-[#ff540c]/35 dark:bg-[#ff540c]/12">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="mt-0.5 text-[#ff540c]" size={16} />
                      <div>
                        <h4 className="text-sm font-medium text-[#7d320d] dark:text-[#ffb08a]">Validation Errors</h4>
                        <ul className="mt-1 space-y-1 text-sm text-[#7d320d] dark:text-[#ffb08a]">
                          {csvValidation.errors.map((validationError, index) => (
                            <li key={index}>• {validationError}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {csvValidation.warnings.length > 0 && (
                  <div className="border border-[#ffb200]/25 bg-[#ffb200]/10 p-4 dark:border-[#ffb200]/35 dark:bg-[#ffb200]/12">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="mt-0.5 text-[#bd7f00] dark:text-[#f4c95d]" size={16} />
                      <div>
                        <h4 className="text-sm font-medium text-[#7b5600] dark:text-[#f4c95d]">Warnings</h4>
                        <ul className="mt-1 space-y-1 text-sm text-[#7b5600] dark:text-[#f4c95d]">
                          {csvValidation.warnings.map((warning, index) => (
                            <li key={index}>• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {csvPreview.length > 0 && csvValidation.isValid && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-[#2d2923] dark:text-[#fdfdfd]">CSV Preview</h4>
                    <div className="overflow-hidden border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.52)] dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
                      <div className="border-b border-[rgba(45,41,35,0.12)] bg-[rgba(31,184,69,0.1)] p-3 dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(31,184,69,0.14)]">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="text-[#1fb845]" size={16} />
                          <span className="text-sm font-medium text-[#1f7a3d] dark:text-[#7ae299]">CSV file is valid</span>
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[rgba(255,255,255,0.64)] dark:bg-[rgba(253,253,253,0.06)]">
                            <tr>
                              {Object.keys(csvPreview[0] || {}).map((header) => (
                                <th key={header} className="px-3 py-2 text-left text-xs font-medium uppercase text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {csvPreview.map((row, index) => (
                              <tr key={index} className="border-b border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)]">
                                {Object.values(row).map((value, cellIndex) => (
                                  <td key={cellIndex} className="px-3 py-2 text-[rgba(45,41,35,0.88)] dark:text-[rgba(253,253,253,0.88)]">
                                    {String(value)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-4">
                  <button
                    onClick={() => navigate('/portfolio')}
                    className="h-[46px] w-full border border-[rgba(45,41,35,0.48)] bg-[rgba(255,255,255,0.72)] text-[14px] font-medium uppercase text-[#2d2923] transition-colors hover:bg-white dark:border-white dark:bg-[rgba(45,41,35,0.72)] dark:text-white dark:hover:bg-[rgba(45,41,35,0.9)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCSVImport}
                    disabled={loading || !csvFile || !csvPortfolioName.trim() || !csvValidation.isValid}
                    className="h-[46px] w-full bg-[#2d2923] text-[14px] font-medium uppercase text-white transition-colors hover:bg-[#201d19] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-[#ecebeb] dark:text-[#2d2923] dark:hover:bg-[#dfdede]"
                  >
                    {loading ? 'Importing...' : 'Import Portfolio'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <aside className="h-fit w-full border border-white/80 bg-white p-6 dark:border-[#1b1919] dark:bg-[#1c1c1c]">
          <AlertCircle className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" size={20} />
          <h3 className="mt-3 font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            What's Next?
          </h3>
          <p className="mt-1 text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            After creating your portfolio, you can add assets manually or import them from a CSV file.
          </p>
        </aside>
      </div>

      <div className="hidden md:block">
      <PageTemplate.Section>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <button
            onClick={() => navigate('/portfolio')}
            className="inline-flex w-fit items-center gap-1.5 border border-[rgba(45,41,35,0.08)] bg-[rgba(255,255,255,0.56)] px-3 py-[6px] text-[12px] font-medium text-[#2d2923] transition-colors hover:bg-white dark:border-[rgba(255,255,255,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]"
          >
            <ArrowLeft size={13} />
            Back to Portfolio
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={downloadCSVTemplate}
              className="inline-flex items-center gap-1.5 border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] px-3 py-[6px] text-[12px] font-medium text-[#2d2923] transition-colors hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]"
            >
              <Download size={13} />
              Download Template
            </button>

            <button
              onClick={() => setActiveTab('csv')}
              className={`inline-flex items-center gap-1.5 border px-3 py-[6px] text-[12px] font-medium transition-colors ${
                activeTab === 'csv'
                  ? 'border-[rgba(45,41,35,0.12)] bg-[#2d2923] text-white hover:bg-[#201d19] dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(253,253,253,0.1)] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.16)]'
                  : 'border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] text-[#2d2923] hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]'
              }`}
            >
              <Upload size={13} />
              Import CSV
            </button>

            <button
              onClick={() => setActiveTab('manual')}
              className={`inline-flex items-center gap-1.5 px-3 py-[6px] text-[12px] font-medium transition-colors ${
                activeTab === 'manual'
                  ? 'bg-[#2d2923] text-white hover:bg-[#201d19] dark:bg-white dark:text-[#0d0d0d] dark:hover:bg-[rgba(253,253,253,0.9)]'
                  : 'border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.72)] text-[#2d2923] hover:bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#0d0d0d] dark:text-[#fdfdfd] dark:hover:bg-[rgba(253,253,253,0.08)]'
              }`}
            >
              <Plus size={13} />
              Manual Creation
            </button>
          </div>
        </div>
      </PageTemplate.Section>

      <PageTemplate.Section>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
          <div className="w-full border border-white/80 bg-[rgba(255,255,255,0.32)] p-5 sm:p-8 xl:flex-1 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
            {activeTab === 'manual' ? (
              <>
                <h1 className="font-plus-jakarta text-[32px] font-semibold leading-[40px] tracking-[-0.42px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Create Portfolio Manually
                </h1>
                <p className="mt-1 text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                  Create an empty portfolio and add assets later
                </p>

                <div className="mt-5 space-y-5">
                  <div>
                    <label className={typography.labelBlock}>Portfolio Name</label>
                    <Input
                      value={portfolioName}
                      onChange={(e) => setPortfolioName(e.target.value)}
                      placeholder="Enter portfolio name"
                      onKeyDown={(e) => e.key === 'Enter' && handleManualCreate()}
                      className={`${formStyles.input} !h-[48px] !rounded-none !shadow-none dark:!bg-[#1c1c1c] dark:!border-[rgba(255,255,255,0.12)] dark:!text-[#fdfdfd] dark:placeholder:!text-[rgba(253,253,253,0.72)]`}
                    />
                  </div>

                  <div>
                    <label className={typography.labelBlock}>Description (Optional)</label>
                    <textarea
                      value={portfolioDescription}
                      onChange={(e) => setPortfolioDescription(e.target.value)}
                      placeholder="Add a description for your portfolio"
                      className="h-[143px] w-full resize-none border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.32)] px-3 py-3 text-[14px] text-[#2d2923] placeholder:text-[rgba(45,41,35,0.64)] focus:outline-none dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1c1c1c] dark:text-[#fdfdfd] dark:placeholder:text-[rgba(253,253,253,0.72)]"
                    />
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={() => navigate('/portfolio')}
                      className="h-[42px] flex-1 border border-[rgba(45,41,35,0.48)] bg-transparent text-[13px] font-medium uppercase tracking-[-0.03px] text-[#2d2923] transition-colors hover:bg-white dark:border-white dark:bg-[rgba(45,41,35,0.72)] dark:text-white dark:hover:bg-[rgba(45,41,35,0.9)]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleManualCreate}
                      disabled={loading || !portfolioName.trim()}
                      className="h-[42px] flex-1 bg-[#2d2923] text-[13px] font-medium uppercase tracking-[-0.03px] text-white transition-colors hover:bg-[#201d19] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-[#ecebeb] dark:text-[#2d2923] dark:hover:bg-[#dfdede]"
                    >
                      {loading ? 'Creating...' : 'Create Portfolio'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h1 className="font-plus-jakarta text-[32px] font-semibold leading-[40px] tracking-[-0.42px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Import Portfolio from CSV
                </h1>
                <p className="mt-1 text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                  Create a portfolio with assets from a CSV file
                </p>

                <div className="mt-5 space-y-5">
                  <div>
                    <label className={typography.labelBlock}>Portfolio Name</label>
                    <Input
                      value={csvPortfolioName}
                      onChange={(e) => setCsvPortfolioName(e.target.value)}
                      placeholder="Enter portfolio name"
                      className={`${formStyles.input} !h-[48px] !rounded-none !shadow-none dark:!bg-[#1c1c1c] dark:!border-[rgba(255,255,255,0.12)] dark:!text-[#fdfdfd] dark:placeholder:!text-[rgba(253,253,253,0.72)]`}
                    />
                  </div>

                  <div>
                    <label className={typography.labelBlock}>CSV File</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="h-[48px] w-full border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.52)] px-3 py-[11px] text-sm text-[#2d2923] file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1c1c1c] dark:text-[#fdfdfd] dark:file:text-[#fdfdfd]"
                    />
                    <p className="mt-1 text-xs text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                      Required columns: Project Name, Date of Investment, Invested Amount
                    </p>
                  </div>

                  {csvValidation.errors.length > 0 && (
                    <div className="border border-[#ff540c]/20 bg-[#ff540c]/8 p-4 dark:border-[#ff540c]/35 dark:bg-[#ff540c]/12">
                      <div className="flex items-start gap-2.5">
                        <AlertCircle className="mt-0.5 text-[#ff540c]" size={16} />
                        <div>
                          <h4 className="text-sm font-medium text-[#7d320d] dark:text-[#ffb08a]">Validation Errors</h4>
                          <ul className="mt-1 space-y-1 text-sm text-[#7d320d] dark:text-[#ffb08a]">
                            {csvValidation.errors.map((validationError, index) => (
                              <li key={index}>• {validationError}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {csvValidation.warnings.length > 0 && (
                    <div className="border border-[#ffb200]/25 bg-[#ffb200]/10 p-4 dark:border-[#ffb200]/35 dark:bg-[#ffb200]/12">
                      <div className="flex items-start gap-2.5">
                        <AlertCircle className="mt-0.5 text-[#bd7f00] dark:text-[#f4c95d]" size={16} />
                        <div>
                          <h4 className="text-sm font-medium text-[#7b5600] dark:text-[#f4c95d]">Warnings</h4>
                          <ul className="mt-1 space-y-1 text-sm text-[#7b5600] dark:text-[#f4c95d]">
                            {csvValidation.warnings.map((warning, index) => (
                              <li key={index}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {csvPreview.length > 0 && csvValidation.isValid && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-[#2d2923] dark:text-[#fdfdfd]">CSV Preview</h4>
                      <div className="overflow-hidden border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.52)] dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
                        <div className="border-b border-[rgba(45,41,35,0.12)] bg-[rgba(31,184,69,0.1)] p-3 dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(31,184,69,0.14)]">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="text-[#1fb845]" size={16} />
                            <span className="text-sm font-medium text-[#1f7a3d] dark:text-[#7ae299]">CSV file is valid</span>
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-[rgba(255,255,255,0.64)] dark:bg-[rgba(253,253,253,0.06)]">
                              <tr>
                                {Object.keys(csvPreview[0] || {}).map((header) => (
                                  <th key={header} className="px-3 py-2 text-left text-xs font-medium uppercase text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {csvPreview.map((row, index) => (
                                <tr key={index} className="border-b border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)]">
                                  {Object.values(row).map((value, cellIndex) => (
                                    <td key={cellIndex} className="px-3 py-2 text-[rgba(45,41,35,0.88)] dark:text-[rgba(253,253,253,0.88)]">
                                      {String(value)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={() => navigate('/portfolio')}
                      className="h-[42px] flex-1 border border-[rgba(45,41,35,0.48)] bg-transparent text-[13px] font-medium uppercase tracking-[-0.03px] text-[#2d2923] transition-colors hover:bg-white dark:border-white dark:bg-[rgba(45,41,35,0.72)] dark:text-white dark:hover:bg-[rgba(45,41,35,0.9)]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCSVImport}
                      disabled={loading || !csvFile || !csvPortfolioName.trim() || !csvValidation.isValid}
                      className="h-[42px] flex-1 bg-[#2d2923] text-[13px] font-medium uppercase tracking-[-0.03px] text-white transition-colors hover:bg-[#201d19] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-[#ecebeb] dark:text-[#2d2923] dark:hover:bg-[#dfdede]"
                    >
                      {loading ? 'Importing...' : 'Import Portfolio'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <aside className="h-fit w-full border border-white/80 bg-white p-6 xl:w-[386px] dark:border-[#1b1919] dark:bg-[#1c1c1c]">
            <AlertCircle className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" size={16} />
            <h3 className="mt-3 font-plus-jakarta text-[32px] font-semibold leading-[40px] tracking-[-0.42px] text-[#2d2923] dark:text-[#fdfdfd]">
              What's Next?
            </h3>
            <p className="mt-1 text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              After creating your portfolio, you can add assets manually or import them from a CSV file.
            </p>
          </aside>
        </div>
      </PageTemplate.Section>
      </div>
      </div>
    </PageTemplate>
  )
}

export default PortfolioCreateScreen
